// Service catalogue. Services + prices stay in code (the coverage data itself
// is CMS-sourced via insurer-cms.ts). Membership tiers are bundled blood tests
// whose coverage reuses each package's blood-test rule (see insurers.ts).
//
// `memberPrice` mirrors MEMBER_PRICING_ITEMS in the membership page's
// membership-client.tsx, which is the catalogue's source of truth (the same
// figures mri-package-cards.tsx already mirrors). The checker keeps its own
// literals rather than reading lib/pricing: every price in this file is a
// literal, and the member figures move on the same rare cadence as the list
// prices next to them. Keep the two lists in step when pricing changes.

import type { Service, Locale } from "./insurance-types"
import { MEMBERSHIP_TIER_PRICES_CHF } from "@/lib/membership-tier-prices"
import { EMBED_SERVICES } from "../proforma/catalog"
import { CATALOG } from "../proforma/catalog.data"

/** Membership service ids. Mirrors `isMembershipService` in booking-deeplink;
 *  kept local so this module stays free of that import cycle. */
const MEMBERSHIP_PREFIX = "membership-"

/** The one product in a membership bundle the insurers' blood-test rules name.
 *  Its follow-up panels are a separate product no stored rule mentions. */
const COVERED_PANEL = "advanced_blood_test"

export const services: Service[] = [
  {
    id: "core-mri",
    name: "Ahead Core (MRI)",
    name_de: "Core MRI (CHF 1'990)",
    name_en: "Core MRI (CHF 1'990)",
    name_fr: "Core IRM (CHF 1'990)",
    price: 1990,
    memberPrice: 1890,
  },
  {
    id: "advanced-mri",
    name: "Ahead Advanced (MRI)",
    name_de: "Advanced MRI (CHF 2'490)",
    name_en: "Advanced MRI (CHF 2'490)",
    name_fr: "Advanced IRM (CHF 2'490)",
    price: 2490,
    memberPrice: 2290,
  },
  {
    id: "pro-mri",
    name: "Ahead Pro (MRI)",
    name_de: "Pro MRI (CHF 3'549)",
    name_en: "Pro MRI (CHF 3'549)",
    name_fr: "Pro IRM (CHF 3'549)",
    price: 3549,
    memberPrice: 3290,
  },
  {
    id: "blood-test",
    name: "Advanced blood test",
    name_de: "Erweiterter Bluttest (CHF 299)",
    name_en: "Advanced blood test (CHF 299)",
    name_fr: "Bilan sanguin avancé (CHF 299)",
    price: 299,
    memberPrice: 279,
  },
  // Membership tiers — bundled blood tests. Coverage reuses each package's
  // blood-test rule via the membership fallback in insurers.ts getCoverage().
  {
    id: "membership-basic",
    name: "Membership 1x per year",
    name_de: `1x pro Jahr (CHF ${MEMBERSHIP_TIER_PRICES_CHF["1x"]})`,
    name_en: `1x per year (CHF ${MEMBERSHIP_TIER_PRICES_CHF["1x"]})`,
    name_fr: `1x par an (CHF ${MEMBERSHIP_TIER_PRICES_CHF["1x"]})`,
    price: MEMBERSHIP_TIER_PRICES_CHF["1x"],
  },
  {
    id: "membership-plus-1",
    name: "Membership 2x per year",
    name_de: `2x pro Jahr (CHF ${MEMBERSHIP_TIER_PRICES_CHF["2x"]})`,
    name_en: `2x per year (CHF ${MEMBERSHIP_TIER_PRICES_CHF["2x"]})`,
    name_fr: `2x par an (CHF ${MEMBERSHIP_TIER_PRICES_CHF["2x"]})`,
    price: MEMBERSHIP_TIER_PRICES_CHF["2x"],
  },
  {
    id: "membership-plus-3",
    name: "Membership 4x per year",
    name_de: `4x pro Jahr (CHF ${MEMBERSHIP_TIER_PRICES_CHF["4x"]})`,
    name_en: `4x per year (CHF ${MEMBERSHIP_TIER_PRICES_CHF["4x"]})`,
    name_fr: `4x par an (CHF ${MEMBERSHIP_TIER_PRICES_CHF["4x"]})`,
    price: MEMBERSHIP_TIER_PRICES_CHF["4x"],
  },
]

export function getServices(): Service[] {
  return services
}

export function findService(id: string): Service | undefined {
  return services.find((s) => s.id === id)
}

/**
 * What the visitor pays for `service`: the member rate when a membership tier
 * is selected alongside it, the list price otherwise.
 *
 * A member buying a Core MRI is invoiced CHF 1'890, so that is the figure the
 * estimate, the reimbursement and the pro forma have to be built on — an
 * insurer reimburses against the invoice, not against the list price. The
 * membership tiers themselves carry no `memberPrice` (a member does not buy a
 * second membership), so this returns their one price either way.
 */
export function effectiveServicePrice(
  service: Service,
  isMember: boolean
): number {
  return isMember && service.memberPrice != null
    ? service.memberPrice
    : service.price
}

/**
 * The part of a membership tier's price an insurer's blood-test rule can pay
 * on. `null` for anything that is not a membership, so a caller can tell
 * "not applicable" from a genuine zero.
 *
 * Not the tier price. A membership is invoiced per visit: the pro forma for a
 * 2x tier renders "Visit 1 of 2 — Advanced blood test 299" and "Visit 2 of 2
 * — Advanced blood test (follow-up) 200", which is what the insurer reads.
 * Every stored blood-test rule is written for the Advanced blood test "once
 * per year" (KPT Pulse Premium: "75%, max. CHF 4,000 for Advanced blood test
 * from Ahead Health ... once per year"), so the rule pays on that one panel
 * and not on the bundle — 224 rather than 374 on a 2x tier at 75%.
 *
 * This applies to a membership bought ON ITS OWN, where that document exists.
 * A membership bought alongside a check-up is a different case: the generator
 * bills one package per document, so only the check-up reaches the insurer
 * and the tier has nothing to claim against (see the membership line in
 * panel-result.ts).
 *
 * Derived from the catalogue rather than hardcoded so a change to what a tier
 * bundles moves this with it.
 */
export function membershipCoverageBase(serviceId: string): number | null {
  // Membership ids only. Gating on "does this package bundle an Advanced
  // blood test" also caught `blood-test`, whose package IS one, and forced
  // that service's base to the list 299 over a member's own 279.
  if (!serviceId.startsWith(MEMBERSHIP_PREFIX)) return null
  const embed = EMBED_SERVICES[serviceId]
  if (!embed) return null
  const pkg = CATALOG.PACKAGES[embed.pkg]
  if (!pkg?.cover_products.includes(COVERED_PANEL)) return null
  return CATALOG.PRODUCT_PRICES[COVERED_PANEL]?.price ?? null
}

export function getServiceName(service: Service, locale: Locale): string {
  if (locale === "en") return service.name_en
  if (locale === "fr") return service.name_fr
  return service.name_de
}
